import { keyframes } from '@emotion/react'
import { CreateFocusCss } from '../../../styles'
import type { Tokens } from '../../../theming/types'
import { styles } from '../../../utilities'
import type { ButtonProps } from '..'

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

export default (config: {
  props: Pick<ButtonProps, 'animate' | 'loading' | 'size' | 'text'>
  tokens: Tokens
}) => {
  const { props, tokens } = config

  return {
    button: {
      default: {
        ...(props.size === 'small' ? tokens.fontButtonS : tokens.fontButton),
        alignItems: 'center',
        borderColor: 'transparent',
        borderStyle: 'solid',
        borderWidth: '1px',
        cursor: props.loading ? 'progress' : 'pointer',
        display: 'inline-flex',
        justifyContent: props.animate ? undefined : 'center',
        letterSpacing: '0.66px',
        margin: 0,
        minHeight: tokens.ctaSizeMd,
        overflow: 'visible',
        paddingBlock: '4px',
        paddingInline: '14px',
        position: 'relative',
        textAlign: 'center',
        textDecoration: 'none',
        textTransform: 'uppercase',
        transitionProperty: 'background-color, border-color, color',
        transitionDuration: '0.1s',
        transitionTimingFunction: 'cubic-bezier(0.33, 0, 0.67, 1)',
        verticalAlign: 'middle',

        '&:lang(ja), &:lang(ko), &:lang(zh)': {
          whiteSpace: 'nowrap',
        },

        '& img': {
          height: '16px',
        },

        // This is for teams that use a version on the icons package before v4.22.1.
        // They do not have the fill="currentColor" attribute on the svg icons before v4.22.1.
        '& svg': {
          fill: 'currentColor',
        },

        ...CreateFocusCss(tokens.focusOuter),
      },

      groupStart: {
        borderRadius: `${tokens.radiusSizeXS} 0px 0px ${tokens.radiusSizeXS}`,
        // Support for logical border-radius is not good. Thus, leave in above fallback.
        borderStartStartRadius: tokens.radiusSizeXS,
        borderStartEndRadius: '0px',
        borderEndEndRadius: '0px',
        borderEndStartRadius: tokens.radiusSizeXS,
        borderInlineEnd: 'none',

        ...CreateFocusCss({
          zIndex: 1,
        }),
      },

      groupCenter: {
        borderRadius: 0,
        borderInlineEnd: 'none',

        ...CreateFocusCss({
          zIndex: 1,
        }),
      },

      groupEnd: {
        borderRadius: `0px ${tokens.radiusSizeXS} ${tokens.radiusSizeXS} 0px`,
        borderStartStartRadius: '0px',
        borderStartEndRadius: tokens.radiusSizeXS,
        borderEndEndRadius: tokens.radiusSizeXS,
        borderEndStartRadius: '0px',

        ...CreateFocusCss({
          zIndex: 1,
        }),
      },

      round: {
        borderRadius: '50%',
        padding: 0,
      },

      fullWidth: {
        width: '100%',
      },

      text: {
        pointerEvents: 'none',
      },
    },

    icon: {
      default: {
        display: 'flex',
        alignItems: 'center',
        pointerEvents: 'none',
      },

      round: {
        margin: 0,
      },

      hideText: {
        margin: 0,
      },
    },

    caret: {
      display: 'inline-flex',
      pointerEvents: 'none',
    },

    hideText: {
      padding: 0,
    },

    hidden: styles.visuallyHidden,

    loadingSpinner: {
      animation: `${spin} 1s infinite linear`,
      borderRadius: '100%',
      borderStyle: 'solid',
      borderTopColor: 'transparent',
      borderWidth: '2px',
      display: 'inline-block',
      flexShrink: 0,
      height: '16px',
      marginInline: '4px',
      marginInlineEnd: props.text ? '12px' : undefined,
      verticalAlign: 'middle',
      width: '16px',
    },

    animation: {
      display: 'inline-flex',
      justifyContent: 'flex-start',
      width: '100%',

      '&[data-animating="true"]': {
        justifyContent: 'center',
      },

      inner: {
        alignItems: 'center',
        display: 'inline-flex',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
    },
  } as const
}
